import { Divider, InputLabel, Stack, styled } from "@mui/material";
import SearchInput from "~/common/components/SearchInput";
import { ImperialToggle } from "~/ImperialMetricData/components/ImperialToggle/ImperialToggle";
import { FilterSortChip } from "~/Search/components/common/mobileChips/FilterSortChip";
import { TechCategoryChip } from "~/Search/components/common/mobileChips/TechCategoryChip";
import { ViewChip } from "~/Search/components/common/mobileChips/ViewChip";
import { SearchDisplayMode } from "~/Search/models";
import ReduxSortingPicker from "../../common/ReduxSortingPicker";
import { DisplayModeButtonList } from "../../common/SearchResultsDisplayMode/SearchResultsDisplayMode";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import type { UnitMode } from "~/ImperialMetricData/redux/UnitMode";
import { SearchPageType, useSearchPageType } from "~/Search/useSearchPageType";
import { useDispatch, useSelector } from "react-redux";
import { handleMissingSortAfterTabChangeThunk, updateSearchText } from "~/Search/redux/actions";
import { trackTreesInteraction } from "~/feature/Analytics/ga/ga";
import { type RootState } from "~/redux/store";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface TopFilterBarProps {
  unitMode: UnitMode;
  displayMode: SearchDisplayMode;
  setUnitMode: (unitMode: UnitMode) => void;
  setDisplayMode: (displayMode: SearchDisplayMode) => void;
}

export const TopFilterBar = ({ unitMode, displayMode, setUnitMode, setDisplayMode }: TopFilterBarProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktop = useIsMinDesktopSize();
  const pageType = useSearchPageType();

  const showTreeButton = pageType !== SearchPageType.Campaign;
  const showQueryInput = pageType === SearchPageType.Campaign;
  const gaInteractionType = pageType === SearchPageType.SEARCH ? "Search" : "PLP";

  const srpStateText = useSelector((state: RootState) => state.srp.text);
  useEffect(() => {
    setSearchQuery(srpStateText);
  }, [srpStateText]);
  const [searchQuery, setSearchQuery] = useState(srpStateText);

  let bar;
  if (!isDesktop) {
    bar = (
      <Stack
        direction="row"
        sx={{
          gap: 1,
          overflow: "auto",
          scrollbarWidth: "none",
        }}
      >
        <ViewChip
          initialUnit={unitMode}
          initialViewMode={displayMode}
          applyValues={(unit, mode) => {
            setUnitMode(unit);
            setDisplayMode(mode);
          }}
        />
        <Divider flexItem orientation="vertical" />

        <FilterSortChip />

        <TechCategoryChip index={0} />
        <TechCategoryChip index={1} />
      </Stack>
    );
  } else {
    const funcDisplayMode = (targetDisplayMode: SearchDisplayMode) => {
      let display: "List" | "Tile" | "Tree";
      if (targetDisplayMode === SearchDisplayMode.LIST) {
        display = "Tile";
        dispatch(handleMissingSortAfterTabChangeThunk());
      } else if (targetDisplayMode === SearchDisplayMode.TABULAR) {
        display = "List";
      } else {
        display = "Tree";
      }
      trackTreesInteraction(gaInteractionType, display);
      setDisplayMode(targetDisplayMode);
    };

    bar = (
      <ActionBar>
        <DisplayModeButtonList
          displayMode={displayMode}
          setDisplayMode={funcDisplayMode}
          showSeriesTree={showTreeButton}
        />

        <ImperialToggle className={"scha__srp__action-bar__unit-toggle"} value={unitMode} onChange={setUnitMode} />

        {displayMode === SearchDisplayMode.LIST && (
          <div>
            <InputLabel shrink>{t("searchPage.sort.title")}</InputLabel>
            <ReduxSortingPicker />
          </div>
        )}
      </ActionBar>
    );
  }

  const submit = () => {
    dispatch(updateSearchText(searchQuery));
  };

  return (
    <>
      {bar}
      {showQueryInput && (
        <SearchInput
          onSearch={submit}
          label={t("searchPage.searchBox.placeholder")}
          onChange={setSearchQuery}
          value={searchQuery}
          sx={{ width: "354px" }}
          size="small"
        />
      )}
    </>
  );
};

const ActionBar = styled("div")({
  display: "grid",
  gridTemplateColumns: "minmax(max-content, 1fr) minmax(max-content, 1fr) 1fr",
  gap: "24px",
  whiteSpace: "nowrap",
});
